import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import NotificationFilterModal from './NotificationFilterModal';

/**
 * Single card for a notification event — shows trigger, human-readable
 * recipient summary, and edit/toggle controls.
 */
export default function NotificationEventCard({ event, moduleKey, onChanged }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.permission_tier === 'super_admin';
  const [editing, setEditing] = useState(false);
  const [toggling, setToggling] = useState(false);

  const enabled = event.is_enabled !== 0;

  async function toggle() {
    if (!isSuperAdmin) return;
    setToggling(true);
    try {
      await api.toggleNotificationEventEnabled(moduleKey, event.id, !enabled);
      if (onChanged) await onChanged();
    } catch (err) {
      alert('Failed to toggle: ' + (err.message || 'unknown error'));
    } finally {
      setToggling(false);
    }
  }

  const summary = summarizeFilter(event.filter_archetype, event.filter);

  return (
    <div style={{
      border: '1px solid var(--border-subtle)',
      borderRadius: 8,
      padding: 16,
      background: enabled ? 'var(--surface-base, #fff)' : 'rgba(107, 114, 128, 0.04)',
      marginBottom: 12,
      opacity: enabled ? 1 : 0.65,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              {event.label}
            </h4>
            {!enabled && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                background: '#6b7280', color: 'white', letterSpacing: '0.04em',
              }}>DISABLED</span>
            )}
          </div>
          {event.description && (
            <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-muted)' }}>
              {event.description}
            </p>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', rowGap: 4, columnGap: 8, fontSize: 13 }}>
            <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>When:</span>
            <span style={{ color: 'var(--text-primary)' }}>{event.trigger_detail || '—'}</span>
            <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Who:</span>
            <span style={{ color: 'var(--text-primary)' }}>{summary}</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          {isSuperAdmin && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={enabled}
                disabled={toggling}
                onChange={toggle}
                style={{ width: 16, height: 16, accentColor: 'var(--brand-gold)' }}
              />
              <span style={{ fontWeight: 600 }}>{enabled ? 'Enabled' : 'Disabled'}</span>
            </label>
          )}
          <button
            className="btn btn-muted btn-sm"
            onClick={() => setEditing(true)}
            style={{ fontSize: 12, padding: '4px 12px' }}
          >
            Edit filter
          </button>
        </div>
      </div>

      {editing && (
        <NotificationFilterModal
          event={event}
          moduleKey={moduleKey}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            if (onChanged) await onChanged();
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// Filter → human summary (display-only)
// ============================================================
function summarizeFilter(archetype, filter) {
  if (!filter) return 'No filter configured';
  try {
    switch (archetype) {
      case 'driver_shift_region': {
        const parts = [];
        if (filter.match_region !== false) parts.push('call region');
        switch (filter.shift_source) {
          case 'due_time': parts.push('shift of due time'); break;
          case 'incoming': parts.push('incoming shift'); break;
          case 'current':
          default: parts.push('current shift'); break;
        }
        if (filter.match_workday !== false) parts.push('active workday');
        const base = `Drivers matching ${parts.join(' + ')}`;
        return filter.emergency_override_shift
          ? `${base} (emergency bypasses shift filter)`
          : base;
      }
      case 'flat_role': {
        const bits = [];
        if (filter.roles?.length) bits.push(`roles: ${filter.roles.join(', ')}`);
        if (filter.tiers?.length) bits.push(`tiers: ${filter.tiers.join(', ')}`);
        if (filter.positions?.length) bits.push(`${filter.positions.length} position(s)`);
        if (filter.user_ids?.length) bits.push(`${filter.user_ids.length} user(s)`);
        return bits.length ? bits.join(' + ') : 'No recipients selected';
      }
      case 'relationship': {
        if (filter.conditional_rules) {
          return 'Conditional — routes by call state + commenter role';
        }
        const map = {
          call_creator: 'creator',
          sales_rep: 'sales rep',
          claimed_driver: 'claimed driver',
          thread_participants: 'thread participants',
          eligible_drivers_shift_region: 'eligible drivers',
        };
        const names = (filter.targets || []).map(t => map[t] || t);
        return names.length ? names.join(' + ') : 'No targets selected';
      }
      default:
        return archetype;
    }
  } catch {
    return 'Could not parse filter';
  }
}
